import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2, Download, Printer, MessageCircle, Tag, Archive, RotateCcw, ChevronDown, Check, History, Sparkles } from "lucide-react";
import ShareSheet from "@/components/ShareSheet";
import PhotoPanel from "./components/PhotoPanel";
import ProductForm from "./components/ProductForm";
import LivePreview from "./components/LivePreview";
import ProposalDialog from "./components/ProposalDialog";
import { AiMark } from "./components/AiMark";
import { buildProposals, applyProposals, setPath, buildScoreProposals, describeScoreRules } from "./components/fieldMap";
import { emptyProduct, previewPng, fetchPdf, saveFile, printFile, STATUS_META, fmtMoney } from "./studioApi";

const DERIVED = ["tax", "band", "rating_warning", "strip_heading", "possible_duplicates", "times_used", "changes", "_flags", "idempotent", "created_by", "created_by_name", "created_at", "updated_at", "updated_by", "revision", "is_deleted", "lot_key", "name_key", "grade_norm", "client_token", "imported_from", "sold_at"];
const stripDerived = (p) => Object.fromEntries(Object.entries(p).filter(([k]) => !DERIVED.includes(k)));
const calcTax = (price, rate) => {
  const p = Number(String(price ?? "").replace(/[^\d.]/g, "")), r = Number(String(rate ?? "").replace(/[^\d.]/g, "")) || 0;
  if (!isFinite(p) || price === "" || price == null) return null;
  const gst = Math.round(p * r) / 100;
  return { base: p.toFixed(2), gst_amount: gst.toFixed(2), inclusive: (p + gst).toFixed(2), rate: String(r) };
};

export default function ProductWorkbench() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [settings, setSettings] = useState(null);
  const [product, setProduct] = useState(null);
  const [savedJson, setSavedJson] = useState("");
  const [saving, setSaving] = useState(false);
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [proposalOpen, setProposalOpen] = useState(false);
  const [firstRun, setFirstRun] = useState(true);
  const [policy, setPolicy] = useState(null);
  const [policyBusy, setPolicyBusy] = useState(false);
  const [dupes, setDupes] = useState([]);
  const [preview, setPreview] = useState({ url: null, overflow: [] });
  const [previewBusy, setPreviewBusy] = useState(false);
  const [orientation, setOrientation] = useState("landscape");
  const [share, setShare] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [scoreBusy, setScoreBusy] = useState(false);
  const [scoreRes, setScoreRes] = useState(null);
  const [scoreProposals, setScoreProposals] = useState([]);
  const [scoreOpen, setScoreOpen] = useState(false);
  const clientToken = useRef(crypto.randomUUID());
  const aiRanFor = useRef(null);
  const previewUrl = useRef(null);

  useEffect(() => {
    api.get("/studio/settings").then((r) => {
      setSettings(r.data);
      if (!id) { const p = emptyProduct(r.data.tax_rate_default); setProduct(p); setSavedJson(JSON.stringify(stripDerived(p))); }
    }).catch(() => toast.error("Could not load Studio settings"));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    api.get(`/studio/products/${id}`).then((r) => {
      const p = { ...emptyProduct(), ...r.data };
      setProduct(p); setSavedJson(JSON.stringify(stripDerived(p))); setAi(p.ai || null); setFirstRun(!p.ai);
      aiRanFor.current = p.photo?.file_id || null;
    }).catch(() => { toast.error("Product not found"); navigate("/studio/library"); });
  }, [id, navigate]);

  const dirty = product ? JSON.stringify(stripDerived(product)) !== savedJson : false;
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const onChange = useCallback((path, value) => {
    setProduct((p) => {
      let np = setPath(p, path, value);
      np.field_sources = { ...(p.field_sources || {}), [path]: "manual" };
      if (path === "overall_rating") np.overall_rating_source = "manual";
      return np;
    });
  }, []);
  const onRating = useCallback((v, source = "manual") => setProduct((p) => ({ ...p, overall_rating: v, overall_rating_source: v == null ? null : source })), []);

  // --- AI reading: auto once per newly uploaded photograph (useEffect on file id, never inline in the upload handler)
  const runAi = useCallback(async (auto = false) => {
    if (!product?.photo?.file_id) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/studio/ai/extract", { file_id: product.photo.file_id, render_file_id: product.photo.render_file_id });
      setAi(data);
      const pristine = !product.name && !product.manufacturer && !product.lot_no;
      const props = buildProposals(data, product);
      setProposals(props);
      setProduct((p) => ({ ...p, ai: { run_at: data.run_at, model: data.model, legibility: data.legibility, flags: data.flags, observations: data.observations, extracted: data.extracted, file_id: p.photo?.file_id }, _flags: data.flags }));
      if (auto && pristine) {
        setProduct((p) => applyProposals({ ...p, _flags: data.flags }, props, new Set(props.map((x) => x.to))));
        setFirstRun(false);
        toast.success(`AI read the photograph — ${props.length} fields filled for your review${data.flags?.length ? `, ${data.flags.length} flagged` : ""}`);
      } else {
        setFirstRun(pristine);
        setProposalOpen(true);
      }
      if (data.extracted?.price_per_kg) toast(`AI read a price “${data.extracted.price_per_kg}” on the label — confirm it in Commercial offer before use.`, { duration: 8000 });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "AI reading failed — you can retry or fill the fields manually");
    } finally {
      setAiBusy(false);
    }
  }, [product]);

  const fileId = product?.photo?.file_id;
  useEffect(() => {
    if (!fileId || aiRanFor.current === fileId) return;
    aiRanFor.current = fileId;
    runAi(true);
  }, [fileId]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- "Let AI score": rules fix the allowed range per card, the model positions inside it; nothing applied until reviewed
  const runScore = async () => {
    setScoreBusy(true);
    try {
      const { data } = await api.post("/studio/ai/score", { file_id: product.photo?.file_id, render_file_id: product.photo?.render_file_id, product: stripDerived(product) });
      setScoreRes(data);
      setScoreProposals(buildScoreProposals(data, product));
      setScoreOpen(true);
      if (!data.ai_used) toast.warning("AI was unavailable — rule-engine centre values are proposed instead", { duration: 7000 });
      else if (data.clamped?.length) toast(`${data.clamped.length} AI value(s) were pulled back inside the policy range`, { duration: 6000 });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "AI scoring failed — please retry");
    } finally { setScoreBusy(false); }
  };

  // --- policy suggestion (advisory; never replaces an existing rating)
  const price = product?.price_per_kg;
  const hint = ai?.observations?.liquor_quality_hint ?? product?.ai?.observations?.liquor_quality_hint ?? null;
  const vis = ai?.observations?.liquor_visibility ?? product?.ai?.observations?.liquor_visibility ?? null;
  useEffect(() => {
    if (!product) return;
    if (price === "" || price == null) { setPolicy(null); return; }
    setPolicyBusy(true);
    const t = setTimeout(() => {
      api.get("/studio/policy/evaluate", { params: { price, liquor_hint: hint ?? undefined, liquor_visibility: vis ?? undefined } }).then((r) => {
        setPolicy(r.data);
        setProduct((p) => (p.overall_rating == null && r.data.suggested != null ? { ...p, overall_rating: r.data.suggested, overall_rating_source: "policy" } : p));
      }).catch(() => {}).finally(() => setPolicyBusy(false));
    }, 400);
    return () => clearTimeout(t);
  }, [price, hint, vis]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- duplicate hints (non-blocking)
  const lot = product?.lot_no, pname = product?.name, grade = product?.grade;
  useEffect(() => {
    if (!product || (!lot && !pname)) { setDupes([]); return; }
    const t = setTimeout(() => api.get("/studio/products/duplicates", { params: { lot_no: lot, name: pname, grade, exclude: id || undefined } }).then((r) => setDupes(r.data)).catch(() => {}), 500);
    return () => clearTimeout(t);
  }, [lot, pname, grade, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- live preview (debounced render of the real PDF page)
  const previewKey = product ? JSON.stringify(stripDerived({ ...product, ai: null, field_sources: null })) : "";
  useEffect(() => {
    if (!product || !product.name) return;
    setPreviewBusy(true);
    const t = setTimeout(async () => {
      try {
        const r = await previewPng({ ...stripDerived(product), status: product.status }, orientation);
        if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
        previewUrl.current = r.url;
        setPreview(r);
      } catch (e) { /* keep last preview */ } finally { setPreviewBusy(false); }
    }, 700);
    return () => clearTimeout(t);
  }, [previewKey, orientation]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!product.name?.trim()) { toast.error("Product name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...stripDerived(product), client_token: clientToken.current };
      const r = id ? await api.put(`/studio/products/${id}`, payload) : await api.post("/studio/products", payload);
      const p = { ...emptyProduct(), ...r.data, field_sources: product.field_sources, _flags: product._flags };
      setProduct(p); setSavedJson(JSON.stringify(stripDerived(p)));
      if (r.data.possible_duplicates?.length) toast.warning("Saved — possible duplicate noted (separate lots are allowed)");
      else toast.success(id ? "Product updated" : "Product saved to the Studio library");
      if (!id) navigate(`/studio/products/${r.data.id}`, { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed — your edits are still here, please retry");
    } finally { setSaving(false); }
  };

  const setStatus = async (status) => {
    try {
      const r = await api.patch(`/studio/products/${id}/status`, { status });
      setProduct((p) => ({ ...p, status: r.data.status })); setSavedJson((s) => JSON.stringify({ ...JSON.parse(s), status: r.data.status }));
      toast.success(status === "sold" ? `Marked SOLD${r.data.catalogues_flagged ? ` — ${r.data.catalogues_flagged} saved catalogue(s) flagged` : ""}` : `Marked ${status}`);
    } catch (e) { toast.error("Could not update status"); }
  };
  const archive = async () => {
    try { await api.delete(`/studio/products/${id}`); toast.success("Archived — recoverable from the library’s Archived view"); navigate("/studio/library"); } catch (e) { toast.error("Could not archive"); }
  };

  const exportPdf = async (o, size, print = false) => {
    try {
      const { file, overflow } = await fetchPdf(`/studio/products/${id}/export`, { orientation: o, size }, "product.pdf");
      if (overflow) toast.warning("Some text was shortened with “…” to keep the tea on one page — shorten the wording to print it in full");
      print ? printFile(file) : saveFile(file);
    } catch (e) { toast.error("Export failed — please retry"); }
  };
  const prepareShare = useMemo(() => () => fetchPdf(`/studio/products/${id}/export`, { orientation, size: "original" }, "product.pdf").then((r) => r.file), [id, orientation]);

  if (!product || !settings) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#153E32]" /></div>;
  const viewProduct = { ...product, tax: calcTax(product.price_per_kg, product.tax_rate) };
  const st = STATUS_META[product.status] || STATUS_META.available;
  const exportDisabled = !id || dirty;
  const canScore = (product.price_per_kg !== "" && product.price_per_kg != null) || (product.overall_rating_source === "manual" && product.overall_rating != null);

  return (
    <div className="pgt-fade-up space-y-4" data-testid="product-workbench">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => { if (dirty) setConfirm("leave"); else navigate(-1); }} data-testid="wb-back"><ArrowLeft className="h-5 w-5" /></Button>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.2em] text-[#AD8642] font-semibold">{id ? "Edit tea product" : "Add tea product"}</div>
            <h1 className="font-display text-3xl text-[#153E32] truncate">{product.name || "New tea"}</h1>
          </div>
          {id && <Badge variant="outline" className={st.cls} data-testid="wb-status">{st.label}</Badge>}
          <AiMark product={product} testid="wb-ai-mark" />
          {dirty && <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300" data-testid="wb-dirty">Unsaved changes</Badge>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={runScore} disabled={scoreBusy || !canScore} className="gap-1 border-[#AD8642] text-[#153E32] hover:bg-[#F1E5C9]/60" data-testid="wb-ai-score"
            title={canScore ? "AI places every card score inside the policy range for your review" : "Enter a base price (or set the overall rating manually) first"}>
            {scoreBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-[#AD8642]" />} {scoreBusy ? "Scoring…" : "Let AI score"}
          </Button>
          {id && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="outline" className="gap-1" data-testid="wb-actions"><Tag className="h-4 w-4" /> Status <ChevronDown className="h-3.5 w-3.5" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {product.status !== "sold" && <DropdownMenuItem onClick={() => setStatus("sold")} data-testid="wb-mark-sold"><Tag className="h-4 w-4 mr-2" /> Mark as Sold</DropdownMenuItem>}
                {product.status !== "available" && <DropdownMenuItem onClick={() => setStatus("available")} data-testid="wb-mark-available"><RotateCcw className="h-4 w-4 mr-2" /> Mark as Available</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setConfirm("archive")} className="text-red-700" data-testid="wb-archive"><Archive className="h-4 w-4 mr-2" /> Archive / delete (recoverable)</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(`/studio/products/${id}/history`)} data-testid="wb-history"><History className="h-4 w-4 mr-2" /> Revision history</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="outline" disabled={exportDisabled} className="gap-1" data-testid="wb-export" title={exportDisabled ? "Save first" : ""}><Download className="h-4 w-4" /> Export <ChevronDown className="h-3.5 w-3.5" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportPdf("landscape", "original")} data-testid="wb-export-landscape">Landscape PDF (original size)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportPdf("landscape", "a4")} data-testid="wb-export-a4">Landscape PDF · fit to A4</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportPdf("portrait", "original")} data-testid="wb-export-portrait">Portrait A4 PDF</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => exportPdf(orientation, "original", true)} data-testid="wb-print"><Printer className="h-4 w-4 mr-2" /> Print</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShare(true)} data-testid="wb-share"><MessageCircle className="h-4 w-4 mr-2 text-[#25D366]" /> Share PDF / WhatsApp</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={save} disabled={saving} className="bg-[#153E32] hover:bg-[#2A5144] gap-1" data-testid="wb-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {id ? "Save changes" : "Save product"}</Button>
        </div>
      </div>

      {ai?.extracted?.price_per_kg && product.price_per_kg === "" && (
        <div className="rounded-lg border border-[#AD8642]/40 bg-[#F1E5C9]/60 px-3 py-2 text-sm text-[#153E32] flex items-center justify-between gap-2 flex-wrap" data-testid="ai-price-chip">
          <span>The AI read a price on the label: <b>{ai.extracted.price_per_kg}</b>. Prices are never applied automatically.</span>
          <Button size="sm" variant="outline" className="h-7" onClick={() => onChange("price_per_kg", String(ai.extracted.price_per_kg).replace(/[^\d.]/g, ""))} data-testid="ai-price-use"><Check className="h-3.5 w-3.5 mr-1" /> Use {fmtMoney(ai.extracted.price_per_kg)}</Button>
        </div>
      )}

      <div className="grid xl:grid-cols-12 gap-5">
        <div className="xl:col-span-3 space-y-4">
          <PhotoPanel photo={product.photo} onPhoto={(meta) => setProduct((p) => ({ ...p, photo: meta }))} ai={ai} aiBusy={aiBusy} onRunAi={() => runAi(false)} flags={product._flags || ai?.flags} />
        </div>
        <div className="xl:col-span-5 xl:order-none order-last">
          <ProductForm product={viewProduct} onChange={onChange} policy={policy} policyBusy={policyBusy} onRating={onRating} settings={settings} isAdmin={isAdmin} dupes={dupes} />
        </div>
        <div className="xl:col-span-4"><div className="xl:sticky xl:top-6"><LivePreview url={preview.url} busy={previewBusy} overflow={preview.overflow} orientation={orientation} onOrientation={setOrientation} /></div></div>
      </div>

      <ProposalDialog open={proposalOpen} onOpenChange={setProposalOpen} proposals={proposals} firstRun={firstRun} sources={product.field_sources}
        onApply={(sel) => { setProduct((p) => applyProposals(p, proposals, sel)); setProposalOpen(false); setFirstRun(false); toast.success(`${sel.size} AI proposal(s) applied`); }} />

      <ProposalDialog open={scoreOpen} onOpenChange={setScoreOpen} proposals={scoreProposals} firstRun={false} sources={product.field_sources} testid="score-dialog"
        title="AI scoring — review before applying" note={describeScoreRules(scoreRes)}
        description="Scores were placed inside the range set by the price policy; captions are AI drafts. Tick what to accept — values you set manually are unticked and never overwritten silently. Nothing here claims the tea was tasted."
        onApply={(sel) => {
          setProduct((p) => {
            const np = applyProposals(p, scoreProposals, sel);
            if (sel.has("overall_rating")) np.overall_rating_source = "policy";
            if (sel.size) np.ai_scoring = { run_at: scoreRes?.run_at || new Date().toISOString(), model: scoreRes?.model, anchor: scoreRes?.anchor, band_label: scoreRes?.band?.label, ai_used: !!scoreRes?.ai_used, applied: sel.size };
            return np;
          });
          setScoreOpen(false); toast.success(`${sel.size} AI score proposal(s) applied`);
        }} />

      {id && <ShareSheet open={share} onOpenChange={setShare} title={`${product.name} profile`} message={`Prime Gold Tea — ${product.name}${product.lot_no ? ` (lot ${product.lot_no})` : ""}: customer product profile attached.`} prepare={prepareShare} />}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="wb-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm === "archive" ? "Archive this product?" : "Leave without saving?"}</AlertDialogTitle>
            <AlertDialogDescription>{confirm === "archive" ? "The product moves to the Archived view and can be restored. Saved catalogue snapshots and the original photograph are kept." : "You have unsaved edits. They will be lost if you leave now."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="wb-confirm-cancel">Stay</AlertDialogCancel>
            <AlertDialogAction onClick={() => { const c = confirm; setConfirm(null); c === "archive" ? archive() : navigate(-1); }} className="bg-[#153E32]" data-testid="wb-confirm-ok">{confirm === "archive" ? "Archive" : "Leave"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
